/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiHideFor,
  EuiIcon,
  EuiLink,
  EuiPanel,
  EuiShowFor,
  EuiSpacer,
  EuiText,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import useGetBasePrices from '@modules/billing-lib/queries/useGetBasePrices'
import useGetInstanceChartData from '@modules/billing-lib/queries/useGetInstanceChartData'
import useGetInstanceCostItems from '@modules/billing-lib/queries/useGetInstanceCostItems'
import { CuiAlert } from '@modules/cui/Alert'
import { colorForInstances } from '@modules/billing-lib/colorGenerator'
import { DocLink } from '@modules/doclinks'

import ChartFilters from '@/components/Billing/BillingUsage/components/Filters/ChartFilters'
import messages from '@/components/Billing/BillingOverview/messages'
import ProductItemList from '@/components/Billing/BillingUsage/BillingUsageByInstance/ProductItemListTable'
import {
  buildServerlessDimensionItems,
  mapToDeploymentCostsItemsV2,
} from '@/components/Billing/BillingUsage/BillingUsageByInstance/utils'
import { ViewByToBucketingStrategy } from '@/components/Billing/BillingUsage/constants'
import InstanceUsageTable from '@/components/Billing/BillingUsage/components/InstanceUsageTable'
import ErrorCallout from '@/components/Billing/BillingUsage/components/ErrorCallout'
import { ProductType } from '@/components/Billing/BillingUsage/types'

import { Action } from '../components/Filters/filtersReducer'
import { getValidationError, stringifyFilters } from '../components/Filters/utils'
import CostsChartByInstance from '../components/charts/CostsChartByInstance'
import withFiltersProvider from '../components/Filters/withFiltersProvider'
import useFilters from '../components/Filters/useFilters'
import { costChartPanelCss } from '../styles'
import Settings from '../components/Filters/Settings'
import ResponsivePanelWrapper from '../components/ResponsivePanelWrapper'
import PricingInfoPanel from '../components/PricingInfoPanel'

import type { AllProps } from '@/components/Billing/BillingUsage/BillingUsageByInstance/types'
import type { FunctionComponent } from 'react'

const BillingUsageByInstance: FunctionComponent<AllProps> = ({
  organizationId,
  instanceId,
  instanceName,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { filters, dispatch } = useFilters()
  const theme = useEuiTheme()

  const areDatesValid = !getValidationError({
    startDate: filters.startDate,
    endDate: filters.endDate,
    viewBy: filters.viewBy,
  })
  const { data: base_prices = [] } = useGetBasePrices()

  // Each time the filters change we want to update queryParams
  useEffect(() => {
    colorForInstances.reset()
    const queryString = stringifyFilters(filters)
    navigate(`${location.pathname}?${queryString}`, { replace: true })
  }, [history, filters])

  const onBackToUsageClick = () =>
    dispatch({ type: Action.SET_INSTANCE_ID, payload: { instanceId: undefined } })

  const instanceCostItems = useGetInstanceCostItems(
    {
      pathParameters: {
        organization_id: organizationId,
        instance_id: instanceId,
      },
      queryParameters: {
        from: filters.startDate.startOf('day').utc().format(),
        to: filters.endDate.endOf('day').utc().format(),
      },
    },
    {
      enabled: areDatesValid,
    },
  )

  const instance_type = instanceCostItems.data?.products[0]?.type

  const renderListTable = () => {
    if (instanceCostItems.isError) {
      return (
        <CuiAlert type='error' data-test-id='error-message'>
          <FormattedMessage {...messages.genericErrorMessage} />
        </CuiAlert>
      )
    }

    if (instance_type === 'deployment') {
      return (
        <EuiPanel
          hasShadow={false}
          paddingSize='l'
          hasBorder={true}
          data-test-id='deployment-usage-table'
        >
          <ResponsivePanelWrapper>
            <InstanceUsageTable itemsCosts={mapToDeploymentCostsItemsV2(instanceCostItems.data)} />
          </ResponsivePanelWrapper>
        </EuiPanel>
      )
    }

    return (
      <ProductItemList
        products={buildServerlessDimensionItems(instanceCostItems.data, base_prices)}
        isLoading={instanceCostItems.isLoading}
        total_cost={instanceCostItems.data ? instanceCostItems.data.total_ecu : 0}
      />
    )
  }

  const instanceChartQuery = useGetInstanceChartData(
    {
      pathParameters: {
        organization_id: organizationId,
        instance_id: instanceId,
      },
      queryParameters: {
        from: filters.startDate.startOf('day').utc().format(),
        to: filters.endDate.endOf('day').utc().format(),
        bucketing_strategy: ViewByToBucketingStrategy[filters.viewBy],
      },
    },
    {
      enabled: areDatesValid,
    },
  )

  const renderChart = () => {
    if (instanceChartQuery.isError) {
      return <ErrorCallout retry={instanceChartQuery.refetch} data-test-id='costs-chart-error' />
    }

    const type = instance_type === 'deployment' ? ProductType.Deployments : ProductType.Projects
    return (
      <EuiPanel
        hasShadow={false}
        paddingSize='m'
        hasBorder={true}
        css={costChartPanelCss(theme)}
        data-test-id='costs-chart-panel'
      >
        <CostsChartByInstance
          data={instanceChartQuery.data?.data || []}
          isLoading={instanceChartQuery.isLoading}
          productType={type}
        />
      </EuiPanel>
    )
  }

  return (
    <EuiFlexGroup data-test-id='billing-usage' gutterSize='l'>
      <EuiFlexItem grow={10}>
        <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <FormattedMessage id='billing-usage.usage-report' defaultMessage='Usage report for' />
            </EuiText>
            <EuiText size='m' data-test-id='billing-usage.page-title'>
              <h3>{instanceName || instanceId}</h3>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiLink onClick={onBackToUsageClick} data-test-id='back-to-usage-link'>
              <EuiIcon type='returnKey' />{' '}
              <FormattedMessage
                id='billing-usage.back-to-usage-link'
                defaultMessage='Back to usage'
              />
            </EuiLink>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size='xl' />

        {/* Mobile Filters - Flyout mode */}
        <EuiShowFor sizes={['xs', 's', 'm']}>
          <Settings isLoading={instanceCostItems.isLoading} flyoutMode={true} />
          <EuiSpacer size='m' />
        </EuiShowFor>

        <ChartFilters isLoading={instanceCostItems.isLoading} />

        <EuiSpacer size='m' />

        {renderChart()}

        <EuiSpacer size='l' />

        <EuiText size='s'>
          <FormattedMessage
            id='billing-deployment-usage-list.description'
            defaultMessage='All cost units are measured in {ecu}.'
            values={{
              ecu: (
                <DocLink link='billingECU'>
                  <FormattedMessage
                    id='ecu-tooltip.page-ecu-unit'
                    defaultMessage='Elastic Consumption Units (ECU)'
                  />
                </DocLink>
              ),
            }}
          />
        </EuiText>

        <EuiSpacer size='xs' />
        {renderListTable()}
        <EuiShowFor sizes={['xs', 's', 'm']}>
          <EuiSpacer size='m' />
          <PricingInfoPanel />
        </EuiShowFor>
      </EuiFlexItem>
      <EuiFlexItem grow={3}>
        <EuiHideFor sizes={['xs', 's', 'm']}>
          <Settings isLoading={instanceCostItems.isLoading} />
        </EuiHideFor>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default withFiltersProvider<AllProps>(BillingUsageByInstance)
